import { useEffect, useRef, useState } from 'react';

import { useFilterQuery } from '@/hooks/use-filter-query';

import { EntitiesFilterFieldsKeys } from '../filters/entities-onboarding-filters-config';

export enum UiEntitiesFilter {
  DUPLICATES = 'DUPLICATES',
  ERRORS = 'ERRORS',
  SELECTED = 'SELECTED',
  WARNINGS = 'WARNINGS',
}

export interface EntitiesQueryFilters {
  uiEntitiesFilter: UiEntitiesFilter | null;
  searchString: string | undefined;
}

interface UseEntitiesQueryFiltersParams {
  selectedCount?: number;
}

export function useEntitiesQueryFilters({
  selectedCount = 0,
}: UseEntitiesQueryFiltersParams = {}): EntitiesQueryFilters {
  const { get, set } = useFilterQuery();

  const preset = get(EntitiesFilterFieldsKeys.PRESET)[0];
  const searchString: string | undefined = get(EntitiesFilterFieldsKeys.SEARCH)[0];

  const [uiEntitiesFilter, setUiEntitiesFilter] = useState<UiEntitiesFilter | null>(null);

  // Sync preset -> state
  useEffect(() => {
    setUiEntitiesFilter(Object.values(UiEntitiesFilter).find(value => value === preset) ?? null);
  }, [preset]);

  // Clear search only when user explicitly selects a non-null filter
  const prevEntitiesFilter = useRef<UiEntitiesFilter | null>(null);
  useEffect(() => {
    if (
      uiEntitiesFilter !== prevEntitiesFilter.current &&
      uiEntitiesFilter !== null // don’t clear search when switching back to All
    ) {
      set(EntitiesFilterFieldsKeys.SEARCH, '');
    }
    prevEntitiesFilter.current = uiEntitiesFilter;
  }, [set, uiEntitiesFilter]);

  // Reset to "All" when last selected row is deselected while "Selected" filter is active
  useEffect(() => {
    if (uiEntitiesFilter === UiEntitiesFilter.SELECTED && selectedCount === 0) {
      set(EntitiesFilterFieldsKeys.PRESET, '');
    }
  }, [selectedCount, uiEntitiesFilter, set]);

  return {
    uiEntitiesFilter,
    searchString,
  };
}
